import { FormEvent, useEffect, useState } from "react";
import { Eye, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminMember, AdminUser, PaginationMeta, getCurrentAdmin, listAdminMembers, logout } from "./api";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

const genderLabels: Record<string, string> = { Male: "男", Female: "女" };

export default function AdminMembers() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [activeCounts, setActiveCounts] = useState({ Active: 0, Inactive: 0 });
  const [verifiedCounts, setVerifiedCounts] = useState({ Verified: 0, Unverified: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [verified, setVerified] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");

  async function load(filters = { Status: status, Verified: verified, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminMembers(filters);
      setMembers(data.Members);
      setActiveCounts(data.Counts?.ActiveStatus || { Active: 0, Inactive: 0 });
      setVerifiedCounts(data.Counts?.VerifiedStatus || { Verified: 0, Unverified: 0 });
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "會員資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Verified: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Verified: verified, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  const totalMembers = activeCounts.Active + activeCounts.Inactive;

  const statusTabs: { value: string; label: string; count: number }[] = [
    { value: "", label: "全部會員", count: totalMembers },
    { value: "Active", label: "啟用中", count: activeCounts.Active },
    { value: "Inactive", label: "已停用", count: activeCounts.Inactive },
  ];

  return (
    <AdminLayout sectionLabel="會員管理" title="會員列表" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {statusTabs.map((tab) => (
          <button
            className={`flex min-w-[104px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${
              status === tab.value ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white text-[#315746]"
            }`}
            key={tab.value}
            onClick={() => {
              setStatus(tab.value);
              load({ Status: tab.value, Verified: verified, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
            }}
            type="button"
          >
            <span className="text-xs font-bold">{tab.label}</span>
            <span className="ml-3 text-lg font-bold">{tab.count}</span>
          </button>
        ))}
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:flex-wrap md:items-end md:p-4" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[140px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="member-verified">Email 驗證</label>
          <select
            className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            id="member-verified"
            value={verified}
            onChange={(event) => setVerified(event.target.value)}
          >
            <option value="">全部</option>
            <option value="Verified">已驗證（{verifiedCounts.Verified}）</option>
            <option value="Unverified">未驗證（{verifiedCounts.Unverified}）</option>
          </select>
        </div>

        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="member-keyword">關鍵字</label>
          <input
            id="member-keyword"
            className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            placeholder="姓名、Email、電話"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
        </div>

        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button
          className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
          onClick={() => load({ Status: status, Verified: verified, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })}
          type="button"
        >
          <RefreshCw size={18} />
          重新整理
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1080px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[220px] px-4 py-3">會員</th>
                <th className="w-[140px] px-4 py-3">性別</th>
                <th className="w-[160px] px-4 py-3">電話</th>
                <th className="w-[120px] px-4 py-3">Email 驗證</th>
                <th className="w-[110px] px-4 py-3">帳號狀態</th>
                <th className="w-[160px] px-4 py-3">註冊時間</th>
                <th className="w-[100px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : members.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>沒有符合條件的會員。</td></tr>
              ) : members.map((member) => (
                <tr className="border-t border-[#edf0ee]" key={member.MemberId}>
                  <td className="px-4 py-4">
                    <p className="font-bold text-[#203d32]">{member.Name}</p>
                    <p className="mt-1 truncate text-xs text-[#7b877f]">{member.Email}</p>
                  </td>
                  <td className="px-4 py-4">{member.Gender ? genderLabels[member.Gender] || member.Gender : "-"}</td>
                  <td className="px-4 py-4">{member.Phone || "-"}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${member.IsEmailVerified ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#f3ecdf] text-[#8a6a2f]"}`}>
                      {member.IsEmailVerified ? "已驗證" : "未驗證"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${member.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>
                      {member.IsActive ? "啟用中" : "已停用"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">{member.CreatedAt || "-"}</td>
                  <td className="px-4 py-4">
                    <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/members/${member.MemberId}`)} type="button">
                      <Eye size={16} />查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Verified: verified, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>
    </AdminLayout>
  );
}
